import * as React from 'react';
import { useState } from 'react';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { Divider, TextField, Button, Stack, Alert } from '@mui/material';
import { useData } from '../Form';
import { useConferenceConfig } from './ConferenceConfigContext';

export default function OrderSummary() {
  const {data, updateData} = useData()
  const { config } = useConferenceConfig()
  const products = data.products

  const [codeInput, setCodeInput] = useState('');
  const [codeError, setCodeError] = useState('');

  let discount = {}
  if (data.hasFinancialSupport) {
    discount = {
      name: 'Financial Support',
      description: 'From: ' + data.financialSupportSource ?? "",
      quantity: 1,
      unit_amount: { value: data.financialSupportAmount ?? 0, currency_code: 'USD' }
    }
  }
  else{
    discount = {}

  }

  const codeAmountOff = Number(data.discountCodeAmountOff) || 0

  function calcSum() {
    let sum = products.reduce((n, { unit_amount }) => n + unit_amount.value, 0)
    if (data.hasFinancialSupport) {
      sum = sum - discount.unit_amount.value
    }
    sum -= codeAmountOff
    return sum
  }

  // Looks up the entered code (case-insensitively) against
  // conferences/{id}.checkoutDiscountCodes and applies it: an amountOff
  // subtracts from the total, waiveLateFee removes the "Late Fee" line item
  // (stashed on removedLateFeeProduct so it can be restored if the code is
  // removed). Separate from discountCodes, which pre-fills financial support
  // via a URL param instead of something typed in here.
  function handleApplyCode() {
    setCodeError('');
    const entered = codeInput.trim();
    if (!entered) return;
    const codes = config.checkoutDiscountCodes || {};
    const match = Object.entries(codes).find(([key]) => key.trim().toUpperCase() === entered.toUpperCase());
    if (!match) {
      setCodeError('That code is not valid.');
      return;
    }
    const [key, codeConfig] = match;
    let nextProducts = products;
    let removedLateFeeProduct = data.removedLateFeeProduct || null;
    if (codeConfig.waiveLateFee) {
      const lateFeeIndex = products.findIndex((p) => p.name === 'Late Fee');
      if (lateFeeIndex !== -1) {
        removedLateFeeProduct = products[lateFeeIndex];
        nextProducts = products.filter((_, i) => i !== lateFeeIndex);
      }
    }
    updateData({
      products: nextProducts,
      discountCode: key,
      discountCodeAmountOff: Number(codeConfig.amountOff) || 0,
      discountCodeWaiveLateFee: !!codeConfig.waiveLateFee,
      discountCodeDescription: codeConfig.description || '',
      removedLateFeeProduct,
    });
    setCodeInput('');
  }

  function handleRemoveCode() {
    let nextProducts = products;
    if (data.removedLateFeeProduct) {
      nextProducts = [...products, data.removedLateFeeProduct];
    }
    updateData({
      products: nextProducts,
      discountCode: '',
      discountCodeAmountOff: 0,
      discountCodeWaiveLateFee: false,
      discountCodeDescription: '',
      removedLateFeeProduct: null,
    });
  }

  return (
    <React.Fragment>
      <Typography variant="h6" gutterBottom>
        Order summary
      </Typography>
      <List disablePadding>
        {products.map((product) => (
          <ListItem key={product.name} sx={{ py: 1, px: 0 }}>
            <ListItemText primary={product.name} secondary={product.description} />
            <Typography variant="body2">{product.unit_amount.value}</Typography>
          </ListItem>
        ))}
        {data.hasFinancialSupport ?
          <ListItem key={discount.name} sx={{ py: 1, px: 0 }}>
            <ListItemText primary={discount.name} secondary={discount.description} />
            <Typography variant="body2">{-discount.unit_amount.value}</Typography>
          </ListItem> : <></>}
        {codeAmountOff > 0 ?
          <ListItem sx={{ py: 1, px: 0 }}>
            <ListItemText primary={`Discount code: ${data.discountCode}`} secondary={data.discountCodeDescription || ''} />
            <Typography variant="body2">{-codeAmountOff}</Typography>
          </ListItem> : <></>}
        <ListItem sx={{ py: 1, px: 0 }}>
          <ListItemText primary="Total" />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            ${calcSum().toFixed(2)}
          </Typography>
        </ListItem>
      </List>
      {data.discountCode ? (
        <Alert severity="success" sx={{ my: 2 }} action={
          <Button color="inherit" size="small" onClick={handleRemoveCode}>Remove</Button>
        }>
          Discount code "{data.discountCode}" applied{data.discountCodeWaiveLateFee ? ' - late fee waived' : ''}.
        </Alert>
      ) : (
        <Stack direction="row" spacing={1} sx={{ my: 2 }} alignItems="flex-start">
          <TextField size="small" label="Discount code" value={codeInput}
            onChange={(e) => setCodeInput(e.target.value)}
            error={!!codeError} helperText={codeError || ' '}
          />
          <Button variant="outlined" onClick={handleApplyCode} disabled={!codeInput.trim()}>
            Apply
          </Button>
        </Stack>
      )}
      <Divider />
    </React.Fragment>
  );
}
